// AdminTransactions — global view of every billing_payment_event across
// all tenants. Filter by tenant or just scroll the list.

import { useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

interface Transaction {
  id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_subdomain: string;
  period_month: string;
  amount_paise: number;
  method: 'razorpay' | 'manual' | 'webhook';
  external_id: string | null;
  paid_at: string | Date;
  note: string | null;
}

const METHOD_COLOR: Record<string, string> = {
  razorpay: 'bg-good-soft text-good',
  manual: 'bg-warn-soft text-warn',
  webhook: 'bg-bg-elev text-ink/60',
};

function format_inr(paise: number): string {
  return (paise / 100).toLocaleString('en-IN');
}

function format_when(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdminTransactions(): ReactElement {
  const [tenant_filter, set_tenant_filter] = useState<string>('');
  const q = useQuery({
    queryKey: ['admin-transactions', tenant_filter],
    queryFn: () =>
      orpc.admin.list_transactions({
        ...(tenant_filter ? { tenant_id: tenant_filter } : {}),
        limit: 200,
      }),
  });
  // For tenant filter dropdown
  const tq = useQuery({
    queryKey: ['admin-tenants', 'all'],
    queryFn: () => orpc.admin.list_tenants({ limit: 200 }),
  });

  const txs = (q.data?.transactions ?? []) as Transaction[];
  const tenants = (tq.data?.tenants ?? []) as Array<{ id: string; name: string; subdomain: string }>;

  const total_paise = txs.reduce((s, t) => s + t.amount_paise, 0);

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium">Transactions</h1>
          <p className="text-xs text-ink/60 mt-1">
            Every payment that's hit the platform. Razorpay-driven and manual entries
            sit in the same ledger.
          </p>
        </div>
        <div className="text-xs text-ink/60 text-right">
          {txs.length} transactions · ₹{format_inr(total_paise)} total
        </div>
      </div>

      <div className="mb-4">
        <label className="text-sm">
          <span className="block mb-1 text-xs text-ink/60">Tenant</span>
          <select
            value={tenant_filter}
            onChange={(e) => set_tenant_filter(e.target.value)}
            className="px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="">All tenants</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.subdomain})
              </option>
            ))}
          </select>
        </label>
      </div>

      {q.isLoading && <div className="text-sm text-ink/60">Loading…</div>}
      {q.error && (
        <div className="text-sm text-bad px-3 py-2 bg-bad-soft rounded">
          {friendly_error(q.error)}
        </div>
      )}

      {!q.isLoading && txs.length === 0 && (
        <div className="bg-surface border border-line rounded-lg p-8 text-center text-sm text-ink/60">
          No transactions yet.
        </div>
      )}

      {txs.length > 0 && (
        <div className="bg-surface border border-line rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg-elev text-ink/60 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Tenant</th>
                <th className="text-left px-4 py-2 font-medium">Period</th>
                <th className="text-left px-4 py-2 font-medium">Amount</th>
                <th className="text-left px-4 py-2 font-medium">Method</th>
                <th className="text-left px-4 py-2 font-medium">Razorpay id</th>
                <th className="text-left px-4 py-2 font-medium">Paid at</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr key={t.id} className="border-t border-line/40">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/tenants/${t.tenant_id}?tab=billing`}
                      className="font-medium hover:text-primary"
                    >
                      {t.tenant_name}
                    </Link>
                    <div className="text-[11px] font-mono text-ink/50">{t.tenant_subdomain}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">{t.period_month}</td>
                  <td className="px-4 py-3 text-ink/80">₹{format_inr(t.amount_paise)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                        METHOD_COLOR[t.method] ?? 'bg-bg-elev text-ink/60'
                      }`}
                    >
                      {t.method}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {t.external_id ? (
                      <a
                        href={`https://dashboard.razorpay.com/app/payments/${t.external_id}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-[11px] font-mono text-primary hover:underline"
                      >
                        {t.external_id}
                      </a>
                    ) : (
                      <span className="text-[11px] text-ink/40">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-ink/60">{format_when(t.paid_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
